import React, { useState, useCallback, useEffect } from "react";
import {
    Alert,
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Popover,
    Checkbox,
    Chip,
    CircularProgress,
    Pagination,
    Grid,
    Divider,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Add as AddIcon,
    OpenInNew as OpenInNewIcon,
    FolderOff as FolderOffIcon,
    Edit as EditIcon,
    DeleteOutline as DeleteIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    Waves as WavesIcon,
    Description as DocumentIcon,
    TrackChanges as ImpactIcon,
    People as PeopleIcon,
} from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { Link as RouterLink } from "react-router-dom";
import useStore from "../../store";
import insightsApi from "../../api/insightsApi";
import ProjectMembersDialog from "./ProjectMembersDialog";

const REPORTS_PAGE_SIZE = 10;
const PROJECTS_PAGE_SIZE = 6;

// What the caller may do with a project, from the card's `my_role` (see
// GET /insights/projects/). A card without the field comes from a BE that
// predates project roles, which let everyone who could see a project edit it.
const canEditProject = (project) =>
    project?.my_role === undefined || ["owner", "editor"].includes(project.my_role);
const canDeleteProject = (project) =>
    project?.my_role === undefined || project.my_role === "owner";
// Who is in a project is for staff and its owners and editors by membership —
// not for org admins as such — so the BE says so per card. Without the field
// (an older BE), fall back to the role.
const canManageMembers = (project) =>
    project?.can_manage_members ?? ["owner", "editor"].includes(project?.my_role);

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function ProjectsPage() {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const activeUser = useStore((state) => state.activeUser);

    // ── Projects list ──
    const [projects, setProjects] = useState([]);
    const [projectsPage, setProjectsPage] = useState(1);
    const [projectsTotalCount, setProjectsTotalCount] = useState(0);
    const [projectsLoading, setProjectsLoading] = useState(false);

    // ── Create project dialog ──
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [projectName, setProjectName] = useState("");
    const [creating, setCreating] = useState(false);

    // ── Reports a project can take as waves (the edit dialog's picker) ──
    const [reports, setReports] = useState([]);
    const [reportsPage, setReportsPage] = useState(1);
    const [reportsTotalCount, setReportsTotalCount] = useState(0);
    const [reportsLoading, setReportsLoading] = useState(false);

    // ── Edit project dialog ──
    // One dialog for the whole card: rename, remove a wave, add reports as new
    // waves. Each action hits its own endpoint immediately (rather than
    // building a diff to submit), so a half-finished edit leaves nothing
    // pending and the dialog always shows what the server actually holds.
    const [editProject, setEditProject] = useState(null);
    const [editName, setEditName] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [removingWaveId, setRemovingWaveId] = useState(null);
    const [reportsToAdd, setReportsToAdd] = useState([]);
    const [addingWaves, setAddingWaves] = useState(false);

    // ── Members dialog ──
    const [membersProject, setMembersProject] = useState(null);

    // ── Delete project dialog ──
    // `pendingCascade` holds the BE's 409 report of what else would be deleted,
    // so the second confirmation can name it instead of guessing.
    const [projectToDelete, setProjectToDelete] = useState(null);
    const [pendingCascade, setPendingCascade] = useState(null);
    const [deletingProject, setDeletingProject] = useState(false);

    // ── Card content lists ──
    // Which card's count chip is open, and where to hang the list off:
    // { anchorEl, projectId, kind }. One popover serves the whole grid — only
    // one list is ever on show — and the project is looked up by id at render
    // so an edit behind the popover is reflected in it.
    const [listPopover, setListPopover] = useState(null);

    // ── Fetch projects ──
    const fetchProjects = useCallback(async (page = 1) => {
        try {
            setProjectsLoading(true);
            const data = await insightsApi.getProjects({ page, page_size: PROJECTS_PAGE_SIZE });
            setProjects(data.results || []);
            setProjectsTotalCount(data.count ?? 0);
            setProjectsPage(page);
        } catch (error) {
            console.error("Failed to load projects:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "projects.failed_to_load_projects", defaultMessage: "Failed to load projects" }),
                { variant: "error" }
            );
        } finally {
            setProjectsLoading(false);
        }
    }, [enqueueSnackbar, intl]);

    useEffect(() => {
        fetchProjects(1);
    }, [fetchProjects]);

    // ── Fetch reports ──
    const fetchReports = useCallback(async (page = 1) => {
        try {
            setReportsLoading(true);
            const data = await insightsApi.getInsightsList({ page, page_size: REPORTS_PAGE_SIZE });
            setReports(data.results || []);
            setReportsTotalCount(data.count ?? 0);
            setReportsPage(page);
        } catch (error) {
            console.error("Failed to load reports:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "projects.failed_to_load_reports", defaultMessage: "Failed to load reports" }),
                { variant: "error" }
            );
        } finally {
            setReportsLoading(false);
        }
    }, [enqueueSnackbar, intl]);

    // ── Create project ──
    const handleOpenCreateDialog = () => {
        setProjectName("");
        setCreateDialogOpen(true);
    };

    const handleCloseCreateDialog = () => {
        setCreateDialogOpen(false);
        setProjectName("");
    };

    const handleCreateProject = async () => {
        const trimmed = (projectName || "").trim();
        if (!trimmed) return;
        try {
            setCreating(true);
            // A project starts empty: reports (waves) and members are added
            // from its card whenever they exist, not in a step forced right
            // after creation — the reports usually do not exist yet.
            await insightsApi.createProject(trimmed);
            handleCloseCreateDialog();
            fetchProjects(1);
            enqueueSnackbar(
                intl.formatMessage({
                    id: "projects.project_created_empty",
                    defaultMessage: "Project created. Add reports and members from its card whenever you are ready.",
                }),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to create project:", error);
            enqueueSnackbar(
                intl.formatMessage({ id: "projects.failed_to_create_project", defaultMessage: "Failed to create project" }),
                { variant: "error" }
            );
        } finally {
            setCreating(false);
        }
    };

    // ── Edit project ────────────────────────────────────────────────────────
    const handleOpenEditDialog = (project) => {
        setEditProject(project);
        setEditName(project.name || "");
        setReportsToAdd([]);
        fetchReports(1);
    };

    const handleCloseEditDialog = () => {
        setEditProject(null);
        setEditName("");
        setReportsToAdd([]);
    };

    // Replace the open dialog's project with the server's version after every
    // mutation, and refresh the card behind it, so wave numbers (which the BE
    // renumbers on removal) never go stale in either place.
    const applyProjectUpdate = (updated) => {
        setEditProject((prev) => (prev ? { ...prev, ...updated } : prev));
        setProjects((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
    };

    const handleRenameProject = async () => {
        const trimmed = (editName || "").trim();
        if (!editProject || !trimmed || trimmed === editProject.name) return;
        try {
            setSavingName(true);
            const updated = await insightsApi.renameProject(editProject.id, trimmed);
            applyProjectUpdate(updated);
            enqueueSnackbar(
                intl.formatMessage({ id: "projects.project_renamed", defaultMessage: "Project renamed" }),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to rename project:", error);
            enqueueSnackbar(
                error.response?.data?.name?.[0]
                || error.response?.data?.error
                || intl.formatMessage({ id: "projects.failed_to_rename_project", defaultMessage: "Failed to rename project" }),
                { variant: "error" }
            );
        } finally {
            setSavingName(false);
        }
    };

    const handleRemoveWave = async (wave) => {
        if (!editProject) return;
        try {
            setRemovingWaveId(wave.id);
            const result = await insightsApi.removeProjectWave(editProject.id, wave.id);
            applyProjectUpdate({ id: editProject.id, waves: result.waves });
            enqueueSnackbar(
                intl.formatMessage(
                    { id: "projects.wave_removed", defaultMessage: "Removed \"{name}\" from the project. The report itself is kept." },
                    { name: wave.report_name || `#${wave.report_id}` }
                ),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to remove wave:", error);
            enqueueSnackbar(
                error.response?.data?.error
                || intl.formatMessage({ id: "projects.failed_to_remove_wave", defaultMessage: "Failed to remove the wave" }),
                { variant: "error" }
            );
        } finally {
            setRemovingWaveId(null);
        }
    };

    const toggleReportToAdd = (report) => {
        setReportsToAdd((prev) => {
            const exists = prev.some((r) => r.id === report.id);
            if (exists) return prev.filter((r) => r.id !== report.id);
            return [...prev, { id: report.id, title: report.survey_title || `Report #${report.id}` }];
        });
    };

    const handleAddWaves = async () => {
        if (!editProject || reportsToAdd.length === 0) return;
        try {
            setAddingWaves(true);
            const result = await insightsApi.addProjectWaves(
                editProject.id,
                reportsToAdd.map((r) => r.id)
            );
            applyProjectUpdate({ id: editProject.id, waves: result.waves });
            setReportsToAdd([]);
            enqueueSnackbar(
                intl.formatMessage(
                    {
                        id: "projects.waves_added",
                        defaultMessage: "{count, plural, one {# wave} other {# waves}} added to the project."
                    },
                    { count: result.added?.length || 0 }
                ),
                { variant: "success" }
            );
        } catch (error) {
            console.error("Failed to add waves:", error);
            enqueueSnackbar(
                error.response?.data?.error
                || intl.formatMessage({ id: "projects.failed_to_add_waves", defaultMessage: "Failed to add waves" }),
                { variant: "error" }
            );
        } finally {
            setAddingWaves(false);
        }
    };

    // ── Delete project ──────────────────────────────────────────────────────
    const handleAskDeleteProject = (project) => {
        setProjectToDelete(project);
        setPendingCascade(null);
    };

    const handleCancelDeleteProject = () => {
        setProjectToDelete(null);
        setPendingCascade(null);
    };

    // Two-step by design: the first attempt goes without `cascade`, and the BE
    // answers 409 listing any non-wave content (documents, Impact Register,
    // stakeholder groups, chat history) it would take. That answer is shown and
    // confirmed before the second, cascading attempt.
    const handleConfirmDeleteProject = async (cascade = false) => {
        if (!projectToDelete) return;
        try {
            setDeletingProject(true);
            const result = await insightsApi.deleteProject(projectToDelete.id, { cascade });
            const kept = result?.reports_kept?.length || 0;
            enqueueSnackbar(
                intl.formatMessage(
                    {
                        id: "projects.project_deleted",
                        defaultMessage: "Project \"{name}\" deleted. {count, plural, =0 {} one {# report was kept} other {# reports were kept}}."
                    },
                    { name: result?.name || projectToDelete.name, count: kept }
                ),
                { variant: "success" }
            );
            handleCancelDeleteProject();
            if (editProject?.id === projectToDelete.id) handleCloseEditDialog();
            // The row count changed, so reload rather than splicing — and step
            // back a page if this was the last card on it.
            const isLastOnPage = projects.length === 1 && projectsPage > 1;
            fetchProjects(isLastOnPage ? projectsPage - 1 : projectsPage);
        } catch (error) {
            const data = error.response?.data;
            if (error.response?.status === 409 && data?.code === "project_has_dependents") {
                // Not a failure: the BE is asking whether the extra content
                // should go too. Keep the dialog open and show what it is.
                setPendingCascade(data);
                return;
            }
            console.error("Failed to delete project:", error);
            enqueueSnackbar(
                data?.error
                || intl.formatMessage({ id: "projects.failed_to_delete_project", defaultMessage: "Failed to delete project" }),
                { variant: "error" }
            );
        } finally {
            setDeletingProject(false);
        }
    };

    // ── Open a project's workspace ──
    // The workspace fetches by project id and renders every wave in the
    // project, so there is no subset to pick before navigating. Names are
    // passed along as navigation state to save the workspace a lookup — a
    // copy, since a new tab drops the state and the workspace has to cope
    // either way.
    const workspacePath = (project) => `/projects/${project.id}`;

    const workspaceState = (project) => ({
        project,
        waves: [...(project?.waves || [])].sort((a, b) => a.wave_count - b.wave_count),
    });

    // ── What a card counts ──
    // One chip per kind of content the project holds. `counts` is the BE's
    // exact count; `items` is the list behind the chip, which for documents and
    // impacts is the preview the BE sends with the card (capped server-side) —
    // hence `count` and `items.length` can differ, and the popover says so.
    // Waves are read off `project.waves`, patched in place after an add or a
    // remove, so the chip never lags the edit dialog.
    const cardLists = (project) => {
        const counts = project?.counts || {};
        const waveItems = project?.waves || [];
        const waveCount = project?.waves ? waveItems.length : (counts.waves ?? 0);
        const documentItems = project?.documents || [];
        const impactItems = project?.impacts || [];
        return [
            {
                kind: "waves",
                count: waveCount,
                items: waveItems,
                icon: <WavesIcon />,
                label: intl.formatMessage(
                    { id: "projects.waves_count", defaultMessage: "{count} Wave(s)" },
                    { count: waveCount }
                ),
                empty: intl.formatMessage({ id: "projects.no_waves_yet", defaultMessage: "This project has no waves." }),
            },
            {
                kind: "documents",
                count: counts.documents ?? documentItems.length,
                items: documentItems,
                icon: <DocumentIcon />,
                label: intl.formatMessage(
                    { id: "projects.documents_count", defaultMessage: "{count} Document(s)" },
                    { count: counts.documents ?? documentItems.length }
                ),
                empty: intl.formatMessage({ id: "projects.no_documents_yet", defaultMessage: "This project has no documents." }),
            },
            {
                kind: "impacts",
                count: counts.impacts ?? impactItems.length,
                items: impactItems,
                icon: <ImpactIcon />,
                label: intl.formatMessage(
                    { id: "projects.impacts_count", defaultMessage: "{count} Impact(s)" },
                    { count: counts.impacts ?? impactItems.length }
                ),
                empty: intl.formatMessage({ id: "projects.no_impacts_yet", defaultMessage: "This project has no impacts." }),
            },
        ];
    };

    // The list the open chip stands for, looked up fresh so it follows the
    // card's own state rather than a snapshot taken when the chip was clicked.
    const openList = listPopover
        ? cardLists(projects.find((p) => p.id === listPopover.projectId))
            .find((list) => list.kind === listPopover.kind)
        : null;

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#00438E" }}>
                    <FormattedMessage id="projects.title" defaultMessage="Projects" />
                </Typography>
                {/* Staff and organization admins create projects; everyone
                    else is added to them as a member. */}
                {(activeUser?.is_staff || activeUser?.is_org_admin) && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                        sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                    >
                        <FormattedMessage id="projects.create_project" defaultMessage="Create Project" />
                    </Button>
                )}
            </Box>

            {/* Projects grid */}
            {projectsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : projects.length === 0 ? (
                <Box
                    sx={{
                        py: 10,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        color: "text.disabled",
                    }}
                >
                    <FolderOffIcon sx={{ fontSize: 56, opacity: 0.35 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "text.secondary" }}>
                        <FormattedMessage id="projects.no_projects" defaultMessage="No Projects" />
                    </Typography>
                </Box>
            ) : (
                <>
                    <Grid container spacing={2}>
                        {projects.map((project) => (
                            <Grid item xs={12} sm={6} md={4} key={project.id}>
                                <Card
                                    elevation={3}
                                    sx={{
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        borderLeft: "4px solid #00438E",
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, color: "#00438E", mb: 0.5 }}>
                                            {project.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(project.created_at)}
                                        </Typography>
                                        <Divider sx={{ my: 1.5 }} />
                                        {/* What the project holds, counted.
                                            Clicking a chip opens the list
                                            behind it — see `listPopover`. */}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                            {cardLists(project).map((list) => {
                                                const isOpen = listPopover?.projectId === project.id
                                                    && listPopover?.kind === list.kind;
                                                return (
                                                    <Chip
                                                        key={list.kind}
                                                        icon={list.icon}
                                                        label={list.label}
                                                        size="small"
                                                        variant="outlined"
                                                        clickable
                                                        aria-haspopup="true"
                                                        aria-expanded={isOpen}
                                                        onClick={(event) => setListPopover({
                                                            anchorEl: event.currentTarget,
                                                            projectId: project.id,
                                                            kind: list.kind,
                                                        })}
                                                        sx={{
                                                            fontSize: "0.7rem",
                                                            cursor: "pointer",
                                                            ...(isOpen && {
                                                                backgroundColor: "rgba(0, 67, 142, 0.08)",
                                                                borderColor: "#00438E",
                                                                color: "#00438E",
                                                            }),
                                                            "& .MuiChip-icon": { fontSize: "0.9rem" },
                                                            "&:hover": {
                                                                backgroundColor: "rgba(0, 67, 142, 0.08)",
                                                                borderColor: "#00438E",
                                                                color: "#00438E",
                                                            },
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                        {/* A new project has no reports yet — say
                                            so, and offer the way in to whoever
                                            may add them. */}
                                        {(project.waves?.length ?? 0) === 0 && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                                    <FormattedMessage
                                                        id="projects.empty_project_hint"
                                                        defaultMessage="No reports in this project yet."
                                                    />
                                                </Typography>
                                                {canEditProject(project) && (
                                                    <Button
                                                        size="small"
                                                        startIcon={<AddIcon />}
                                                        onClick={() => handleOpenEditDialog(project)}
                                                        sx={{ color: "#00438E", px: 0.5 }}
                                                    >
                                                        <FormattedMessage id="projects.add_reports" defaultMessage="Add reports" />
                                                    </Button>
                                                )}
                                            </Box>
                                        )}
                                    </CardContent>
                                    <CardActions sx={{ px: 2, pb: 2 }}>
                                        {/* A router Link, which renders a real
                                            <a href> — so the context menu can
                                            open the workspace in a new tab —
                                            and routes in-app on a plain click,
                                            modifier clicks left to the browser. */}
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            component={RouterLink}
                                            to={workspacePath(project)}
                                            state={workspaceState(project)}
                                            startIcon={<OpenInNewIcon />}
                                            sx={{
                                                borderColor: "#00438E",
                                                color: "#00438E",
                                                "&:hover": { backgroundColor: "rgba(0,67,142,0.06)" },
                                            }}
                                        >
                                            <FormattedMessage id="projects.open" defaultMessage="Open" />
                                        </Button>
                                        <Box sx={{ flexGrow: 1 }} />
                                        {/* Only those who manage members see
                                            the list (staff, owners, editors). */}
                                        {canManageMembers(project) && (
                                            <Tooltip title={intl.formatMessage({ id: "projects.members", defaultMessage: "Members" })}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setMembersProject(project)}
                                                    aria-label={intl.formatMessage({ id: "projects.members", defaultMessage: "Members" })}
                                                    sx={{ color: "#00438E" }}
                                                >
                                                    <PeopleIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canEditProject(project) && (
                                            <Tooltip title={intl.formatMessage({ id: "projects.edit_project", defaultMessage: "Edit project" })}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditDialog(project)}
                                                    aria-label={intl.formatMessage({ id: "projects.edit_project", defaultMessage: "Edit project" })}
                                                    sx={{ color: "#00438E" }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canDeleteProject(project) && (
                                            <Tooltip title={intl.formatMessage({ id: "projects.delete_project", defaultMessage: "Delete project" })}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleAskDeleteProject(project)}
                                                    aria-label={intl.formatMessage({ id: "projects.delete_project", defaultMessage: "Delete project" })}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    {projectsTotalCount > PROJECTS_PAGE_SIZE && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <Pagination
                                count={Math.ceil(projectsTotalCount / PROJECTS_PAGE_SIZE)}
                                page={projectsPage}
                                onChange={(_, page) => fetchProjects(page)}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}

            {/* ── The list behind a card's count chip ── */}
            {/* Waves are links: a wave IS a report, and its page is a route of
                its own, so each row is a router Link — a real <a href> that
                the context menu can open in a new tab. Documents and impacts
                have no page of their own (they live inside the project
                workspace), so they are named here, not linked. */}
            <Popover
                open={Boolean(listPopover) && Boolean(openList)}
                anchorEl={listPopover?.anchorEl ?? null}
                onClose={() => setListPopover(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{ paper: { sx: { maxWidth: 360, maxHeight: 340, mt: 0.5 } } }}
            >
                {openList && (
                    openList.items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                            {openList.empty}
                        </Typography>
                    ) : (
                        <>
                            <List dense disablePadding>
                                {openList.kind === "waves"
                                    ? openList.items.map((wave) => (
                                        <ListItemButton
                                            key={wave.id ?? wave.wave_count}
                                            component={RouterLink}
                                            to={`/insights/${wave.report_id}`}
                                            onClick={() => setListPopover(null)}
                                            sx={{ gap: 1 }}
                                        >
                                            <ListItemText
                                                primary={intl.formatMessage(
                                                    { id: "projects.wave_n", defaultMessage: "Wave {n}" },
                                                    { n: wave.wave_count }
                                                )}
                                                secondary={wave.report_name}
                                                primaryTypographyProps={{ variant: "body2", fontWeight: 600, color: "#00438E" }}
                                                secondaryTypographyProps={{ variant: "caption" }}
                                            />
                                            <OpenInNewIcon sx={{ fontSize: "0.9rem", color: "#00438E", flexShrink: 0 }} />
                                        </ListItemButton>
                                    ))
                                    : openList.items.map((item) => (
                                        <ListItem key={item.id} sx={{ py: 0.5 }}>
                                            <ListItemText
                                                primary={openList.kind === "documents" ? item.name : item.what_is_changing}
                                                secondary={openList.kind === "documents"
                                                    ? formatDate(item.created_at)
                                                    : [item.group_name, item.level].filter(Boolean).join(" · ")}
                                                primaryTypographyProps={{ variant: "body2" }}
                                                secondaryTypographyProps={{ variant: "caption" }}
                                            />
                                        </ListItem>
                                    ))}
                            </List>
                            {/* The BE caps the documents and impacts it sends
                                with a card, so a big project's list is a
                                preview and has to say so. */}
                            {openList.count > openList.items.length && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}
                                >
                                    <FormattedMessage
                                        id="projects.list_truncated"
                                        defaultMessage="Showing the first {shown} of {count} — open the project for the rest."
                                        values={{ shown: openList.items.length, count: openList.count }}
                                    />
                                </Typography>
                            )}
                        </>
                    )
                )}
            </Popover>

            {/* ── Create Project Dialog ── */}
            <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <FormattedMessage id="projects.create_project_dialog_title" defaultMessage="Create Project" />
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        label={intl.formatMessage({ id: "projects.project_name_label", defaultMessage: "Project name" })}
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseCreateDialog}>
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateProject}
                        disabled={!projectName.trim() || creating}
                        startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                        sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                    >
                        <FormattedMessage id="projects.create" defaultMessage="Create" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Project Dialog ── */}
            {/* Rename, remove a wave, add reports as waves. Every action is
                applied immediately against its own endpoint, so there is no
                "unsaved changes" state to reconcile on close. */}
            <Dialog open={!!editProject} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    <FormattedMessage id="projects.edit_project_dialog_title" defaultMessage="Edit Project" />
                </DialogTitle>
                <DialogContent dividers>
                    {/* Name */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <FormattedMessage id="projects.project_name_label" defaultMessage="Project name" />
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 3 }}>
                        <TextField
                            fullWidth
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleRenameProject()}
                            disabled={savingName}
                        />
                        <Button
                            variant="contained"
                            onClick={handleRenameProject}
                            disabled={
                                savingName
                                || !editName.trim()
                                || editName.trim() === editProject?.name
                            }
                            startIcon={savingName ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                            sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" }, flexShrink: 0 }}
                        >
                            <FormattedMessage id="projects.save_name" defaultMessage="Save" />
                        </Button>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Current waves */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <FormattedMessage
                            id="projects.current_waves"
                            defaultMessage="Waves ({count})"
                            values={{ count: editProject?.waves?.length ?? 0 }}
                        />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        <FormattedMessage
                            id="projects.remove_wave_hint"
                            defaultMessage="Removing a wave takes the report out of this comparison. The report itself is not deleted."
                        />
                    </Typography>
                    <List dense sx={{ border: "1px solid #e0e0e0", borderRadius: 1, mb: 3 }}>
                        {(editProject?.waves || []).map((wave) => {
                            return (
                                <ListItem key={wave.id ?? wave.wave_count} sx={{ pr: 1 }}>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2">
                                                <strong>
                                                    <FormattedMessage
                                                        id="projects.wave_n"
                                                        defaultMessage="Wave {n}"
                                                        values={{ n: wave.wave_count }}
                                                    />
                                                </strong>{" "}
                                                — {wave.report_name || `#${wave.report_id}`}
                                            </Typography>
                                        }
                                    />
                                    <Tooltip title={intl.formatMessage({ id: "projects.remove_wave", defaultMessage: "Remove from project" })}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleRemoveWave(wave)}
                                                disabled={removingWaveId === wave.id}
                                                aria-label={intl.formatMessage({ id: "projects.remove_wave", defaultMessage: "Remove from project" })}
                                            >
                                                {removingWaveId === wave.id
                                                    ? <CircularProgress size={16} />
                                                    : <CloseIcon fontSize="small" />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                        {(editProject?.waves?.length ?? 0) === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" color="text.secondary">
                                            <FormattedMessage id="projects.no_waves_yet" defaultMessage="This project has no waves." />
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )}
                    </List>

                    <Divider sx={{ mb: 2 }} />

                    {/* Add reports as waves. Left out: reports already in the
                        project (the BE rejects a duplicate anyway) and reports
                        the caller reads only through some project's membership
                        (`can_add_to_project` false — adding one would share it
                        further, which the BE refuses). */}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        <FormattedMessage id="projects.add_waves" defaultMessage="Add reports as waves" />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        <FormattedMessage
                            id="projects.add_waves_shares_hint"
                            defaultMessage="Every member of this project will be able to see the reports you add."
                        />
                    </Typography>
                    {reportsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <>
                            <List dense sx={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
                                {reports
                                    .filter((report) => report.can_add_to_project !== false)
                                    .filter((report) => !(editProject?.waves || []).some((w) => w.report_id === report.id))
                                    .map((report) => {
                                        const isSelected = reportsToAdd.some((r) => r.id === report.id);
                                        return (
                                            <ListItem
                                                key={report.id}
                                                button
                                                onClick={() => toggleReportToAdd(report)}
                                                sx={{ bgcolor: isSelected ? "rgba(0, 67, 142, 0.06)" : "transparent" }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    sx={{ color: "#00438E", "&.Mui-checked": { color: "#00438E" }, p: 0.5 }}
                                                />
                                                <ListItemText
                                                    primary={report.survey_title || `Report #${report.id}`}
                                                    secondary={report.survey_created ? formatDate(report.survey_created) : `ID: ${report.id}`}
                                                />
                                            </ListItem>
                                        );
                                    })}
                            </List>
                            {reportsTotalCount > REPORTS_PAGE_SIZE && (
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                                    <Pagination
                                        count={Math.ceil(reportsTotalCount / REPORTS_PAGE_SIZE)}
                                        page={reportsPage}
                                        onChange={(_, page) => fetchReports(page)}
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                            )}
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleAddWaves}
                                    disabled={reportsToAdd.length === 0 || addingWaves}
                                    startIcon={addingWaves ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                    sx={{ backgroundColor: "#00438E", "&:hover": { backgroundColor: "#003A75" } }}
                                >
                                    <FormattedMessage
                                        id="projects.add_selected_waves"
                                        defaultMessage="Add {count, plural, =0 {waves} one {# wave} other {# waves}}"
                                        values={{ count: reportsToAdd.length }}
                                    />
                                </Button>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {canDeleteProject(editProject) && (
                        <Button
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleAskDeleteProject(editProject)}
                        >
                            <FormattedMessage id="projects.delete_project" defaultMessage="Delete project" />
                        </Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={handleCloseEditDialog}>
                        <FormattedMessage id="projects.done" defaultMessage="Done" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete Project Dialog ── */}
            <Dialog open={!!projectToDelete} onClose={handleCancelDeleteProject} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <FormattedMessage id="projects.delete_project_title" defaultMessage="Delete this project?" />
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        <FormattedMessage
                            id="projects.delete_project_confirm"
                            defaultMessage='"{name}" and its waves will be deleted. The reports themselves are kept.'
                            values={{ name: projectToDelete?.name || "" }}
                        />
                    </Typography>
                    {pendingCascade && (
                        <Alert severity="warning" sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                <FormattedMessage
                                    id="projects.delete_project_dependents_title"
                                    defaultMessage="This project also holds content that is not a wave:"
                                />
                            </Typography>
                            <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                {Object.entries(pendingCascade.dependents || {}).map(([key, count]) => (
                                    <li key={key}>
                                        <Typography variant="body2">
                                            {count}{" "}
                                            <FormattedMessage
                                                id={`projects.dependent_${key}`}
                                                defaultMessage={key.replace(/_/g, " ")}
                                            />
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <FormattedMessage
                                    id="projects.delete_project_dependents_note"
                                    defaultMessage="Deleting the project removes these too, and they cannot be recovered."
                                />
                            </Typography>
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCancelDeleteProject} disabled={deletingProject}>
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleConfirmDeleteProject(!!pendingCascade)}
                        disabled={deletingProject}
                        startIcon={deletingProject ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                    >
                        {pendingCascade
                            ? <FormattedMessage id="projects.delete_everything" defaultMessage="Delete everything" />
                            : <FormattedMessage id="projects.delete" defaultMessage="Delete" />}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Members Dialog ── */}
            <ProjectMembersDialog
                project={membersProject}
                currentUserEmail={activeUser?.email}
                onClose={() => setMembersProject(null)}
                onLeft={() => {
                    setMembersProject(null);
                    fetchProjects(projectsPage);
                }}
            />
        </Box>
    );
}
